import * as i2 from '@angular/animations';
import { style, animate } from '@angular/animations';
import * as i2$1 from '@angular/cdk/overlay';
import { OverlayModule, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { HostBinding, Component, ANIMATION_MODULE_TYPE, Inject, Injectable, ElementRef, Renderer2, HostListener, Input, Directive, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { noop, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/cdk/a11y';

class WindmillTooltipComponent {
    hasAriaHidden = true;
    tooltipColor = '';
    tooltipText = '';
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTooltipComponent, isStandalone: true, selector: "windmill-tooltip", host: { properties: { "attr.aria-hidden": "this.hasAriaHidden", "class": "this.tooltipColor" } }, ngImport: i0, template: "{{ tooltipText }}\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "ngmodule", type: OverlayModule }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-tooltip', imports: [CommonModule, OverlayModule], standalone: true, template: "{{ tooltipText }}\r\n" }]
        }], propDecorators: { hasAriaHidden: [{
                type: HostBinding,
                args: ['attr.aria-hidden']
            }], tooltipColor: [{
                type: HostBinding,
                args: ['class']
            }] } });

class TooltipA11yParametersService {
    focusMonitor;
    ariaDescriber;
    animationMode;
    animationBuilder;
    constructor(focusMonitor, ariaDescriber, animationMode, animationBuilder) {
        this.focusMonitor = focusMonitor;
        this.ariaDescriber = ariaDescriber;
        this.animationMode = animationMode;
        this.animationBuilder = animationBuilder;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TooltipA11yParametersService, deps: [{ token: i1.FocusMonitor }, { token: i1.AriaDescriber }, { token: ANIMATION_MODULE_TYPE }, { token: i2.AnimationBuilder }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TooltipA11yParametersService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: TooltipA11yParametersService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.FocusMonitor }, { type: i1.AriaDescriber }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }, { type: i2.AnimationBuilder }] });

const FADE_ANIMATION_DURATION = 120;
const DEFAULT_HIDE_DELAY = 250;
const NO_HIDE_DELAY = 0;
const SCROLL_THROTTLE_MS = 10;
const POSITIONS = {
    left: {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center'
    },
    right: {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center'
    },
    above: {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom'
    },
    below: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }
};
/**
 * To use this component in a module, you can import the `WindmillTooltipModule`:
 * @example
 * ```typescript
 * import { WindmillTooltipModule } from '@windmill/ng-windmill/tooltip';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillTooltipDirective`:
 * @example
 * ```typescript
 * import { WindmillTooltipDirective } from '@windmill/ng-windmill/tooltip';
 * ```
 */
class WindmillTooltipDirective {
    tooltipA11yParametersService;
    overlay;
    elementRef;
    renderer;
    /**
     * The text to be displayed in the tooltip
     * */
    set tooltipText(value) {
        const previousTooltipText = this._tooltipText;
        this._tooltipText = value;
        this.setAriaDescription(this.tooltipText, previousTooltipText);
        if (this.tooltipInstance) {
            this.tooltipInstance.tooltipText = value;
            setTimeout(() => {
                this.overlayRef?.updatePositionStrategy(this.getPositionStrategy());
            });
        }
    }
    get tooltipText() {
        return this._tooltipText;
    }
    /**
     * The position for the tooltip
     * */
    set tooltipPosition(value) {
        this._tooltipPosition = value;
        if (this.tooltipInstance) {
            this.overlayRef?.updatePositionStrategy(this.getPositionStrategy());
        }
    }
    get tooltipPosition() {
        return this._tooltipPosition;
    }
    /**
     * The delay in ms before showing the tooltip
     * */
    set tooltipShowDelay(value) {
        this._tooltipShowDelay = value;
    }
    get tooltipShowDelay() {
        return this._tooltipShowDelay;
    }
    /**
     * The delay in ms before hiding the tooltip
     * */
    set tooltipHideDelay(value) {
        this._tooltipHideDelay = value;
    }
    get tooltipHideDelay() {
        return this._tooltipHideDelay;
    }
    /**
     * The class names you can add to the tooltip overlay element
     * */
    set tooltipClassName(value) {
        const previousClassName = this._tooltipClassName;
        this._tooltipClassName = value;
        this.overlayRef?.removePanelClass(previousClassName);
        this.overlayRef?.addPanelClass(value);
    }
    get tooltipClassName() {
        return this._tooltipClassName;
    }
    /**
     * The color of the tooltip
     * */
    set tooltipColor(value) {
        this._tooltipColor = value;
        if (this.tooltipInstance) {
            this.tooltipInstance.tooltipColor = value;
        }
    }
    get tooltipColor() {
        return this._tooltipColor;
    }
    /**
     * Whether the tooltip should show or not on interaction
     * */
    set isTooltipDisabled(value) {
        this._isTooltipDisabled = value;
        this.setAriaDescription(this.tooltipText);
    }
    get isTooltipDisabled() {
        return this._isTooltipDisabled;
    }
    overlayRef;
    tooltipInstance;
    elementToHaveTooltip;
    tooltipElement = null;
    tooltipMouseLeaveListener = noop;
    showTimeoutID;
    hideTimeoutID;
    animationMode = '';
    fadeInFactory;
    fadeOutFactory;
    fadeInPlayer;
    fadeOutPlayer;
    tooltipFocusMonitorSubscription = Subscription.EMPTY;
    _tooltipText = '';
    _tooltipPosition = 'below';
    _tooltipShowDelay = 0;
    _tooltipHideDelay = DEFAULT_HIDE_DELAY;
    _tooltipClassName = '';
    _tooltipColor = 'neutral';
    _isTooltipDisabled = false;
    constructor(tooltipA11yParametersService, overlay, elementRef, renderer) {
        this.tooltipA11yParametersService = tooltipA11yParametersService;
        this.overlay = overlay;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.animationMode = this.tooltipA11yParametersService.animationMode;
    }
    ngAfterViewInit() {
        this.elementToHaveTooltip =
            this.containsInteractiveElements().length > 0 ? this.containsInteractiveElements()[0] : this.elementRef.nativeElement;
        this.setAriaDescription(this.tooltipText);
        this.handleKeyboardInteraction();
    }
    ngOnDestroy() {
        if (this.isTooltipDisabled) {
            this.tooltipFocusMonitorSubscription.unsubscribe();
            return;
        }
        this.mouseLeaveListener();
        if (this.overlayRef) {
            this.hide(NO_HIDE_DELAY);
        }
        this.destroyAccessibilityServices();
        this.destroyAnimation();
    }
    show() {
        if (!this.isTooltipDisabled) {
            this.clearAllTimeouts();
            if (this.fadeOutPlayer) {
                this.fadeOutPlayer.finish();
            }
            this.mouseLeaveListener();
            this.showTimeoutID = setTimeout(() => {
                this.drawOverlay();
            }, this.tooltipShowDelay);
        }
    }
    handleEscapeKey() {
        this.hide(NO_HIDE_DELAY);
    }
    hide(delay) {
        this.clearAllTimeouts();
        this.hideTimeoutID = setTimeout(() => {
            if (this.overlayRef?.hasAttached()) {
                this.buildAndPlayAnimation(this.fadeOut(), 'fade-out');
            }
        }, delay || delay === 0 ? delay : this.tooltipHideDelay);
    }
    onMouseLeave(event) {
        const overlayElement = this.overlayRef?.overlayElement;
        if (!overlayElement) {
            this.clearAllTimeouts();
            return;
        }
        const relatedTarget = event.relatedTarget;
        if (!relatedTarget || !overlayElement.contains(relatedTarget)) {
            this.hide();
        }
        if (this.tooltipElement) {
            this.tooltipMouseLeaveListener = this.renderer.listen(overlayElement, 'mouseleave', () => {
                this.hide();
            });
        }
    }
    mouseLeaveListener() {
        if (this.tooltipMouseLeaveListener) {
            this.tooltipMouseLeaveListener();
        }
    }
    containsInteractiveElements() {
        return this.elementRef.nativeElement.querySelectorAll('button, a, [tabindex="0"]');
    }
    handleKeyboardInteraction() {
        this.tooltipFocusMonitorSubscription = this.tooltipA11yParametersService.focusMonitor
            .monitor(this.elementToHaveTooltip)
            .subscribe(origin => {
            if (!origin) {
                this.clearAllTimeouts();
                this.hide(NO_HIDE_DELAY);
                if (this.elementToHaveTooltip.classList.value === '') {
                    this.elementToHaveTooltip.removeAttribute('class');
                }
            }
            else if (origin === 'keyboard') {
                this.show();
            }
        });
    }
    drawOverlay() {
        if (!this.overlayRef?.hasAttached()) {
            this.createOverlayWithConfig();
            const tooltipPortal = new ComponentPortal(WindmillTooltipComponent);
            this.tooltipInstance = this.overlayRef.attach(tooltipPortal).instance;
            this.closeOnPointerInteraction();
            this.updateTooltipUI();
            this.tooltipElement = this.overlayRef?.overlayElement.querySelector('windmill-tooltip');
            this.buildAndPlayAnimation(this.fadeIn(), 'fade-in');
        }
    }
    createOverlayWithConfig() {
        this.overlayRef = this.overlay.create(new OverlayConfig({
            panelClass: this.tooltipClassName,
            scrollStrategy: this.overlay.scrollStrategies.reposition({ scrollThrottle: SCROLL_THROTTLE_MS }),
            positionStrategy: this.getPositionStrategy()
        }));
    }
    getPositionStrategy() {
        return this.overlay
            .position()
            .flexibleConnectedTo(this.elementToHaveTooltip)
            .withPositions(this.getPositionsWithFallback())
            .withFlexibleDimensions(false)
            .withPush(true);
    }
    closeOnPointerInteraction() {
        this.overlayRef.outsidePointerEvents().subscribe(() => {
            if (this.fadeOutPlayer) {
                this.fadeOutPlayer.pause();
            }
            this.hide(NO_HIDE_DELAY);
        });
    }
    updateTooltipUI() {
        this.tooltipInstance.tooltipText = this.tooltipText;
        this.tooltipInstance.tooltipColor = this.tooltipColor;
    }
    getPositionsWithFallback() {
        switch (this.tooltipPosition) {
            case 'left':
                return [POSITIONS.left, POSITIONS.right, POSITIONS.below, POSITIONS.above];
            case 'right':
                return [POSITIONS.right, POSITIONS.left, POSITIONS.below, POSITIONS.above];
            case 'below':
                return [POSITIONS.below, POSITIONS.above, POSITIONS.right, POSITIONS.left];
            case 'above':
                return [POSITIONS.above, POSITIONS.below, POSITIONS.right, POSITIONS.left];
            default:
                return [POSITIONS.below, POSITIONS.above, POSITIONS.right, POSITIONS.left];
        }
    }
    clearTimeoutByID(timeoutID) {
        if (timeoutID) {
            clearTimeout(timeoutID);
        }
    }
    clearAllTimeouts() {
        this.clearTimeoutByID(this.showTimeoutID);
        this.clearTimeoutByID(this.hideTimeoutID);
    }
    setAriaDescription(value, previousValue) {
        if (this.elementToHaveTooltip) {
            if (previousValue) {
                this.tooltipA11yParametersService.ariaDescriber.removeDescription(this.elementToHaveTooltip, previousValue, 'tooltip');
            }
            else {
                this.tooltipA11yParametersService.ariaDescriber.removeDescription(this.elementToHaveTooltip, value, 'tooltip');
            }
            if (!this.isTooltipDisabled) {
                this.tooltipA11yParametersService.ariaDescriber.describe(this.elementToHaveTooltip, value, 'tooltip');
            }
        }
    }
    buildAndPlayAnimation(animationMetadata, animationName) {
        switch (animationName) {
            case 'fade-in':
                if (this.animationMode === 'NoopAnimations') {
                    return;
                }
                this.fadeInFactory = this.tooltipA11yParametersService.animationBuilder.build(animationMetadata);
                this.fadeInPlayer = this.fadeInFactory.create(this.tooltipElement);
                this.fadeInPlayer.play();
                break;
            case 'fade-out':
                if (this.animationMode === 'NoopAnimations') {
                    this.removeOverlay();
                    return;
                }
                this.fadeOutFactory = this.tooltipA11yParametersService.animationBuilder.build(animationMetadata);
                this.fadeOutPlayer = this.fadeOutFactory.create(this.tooltipElement);
                this.fadeOutPlayer.play();
                this.fadeOutPlayer.onDone(() => this.removeOverlay());
                break;
            default:
                break;
        }
    }
    fadeIn() {
        return [
            style({ opacity: 0, transform: 'scale(0.8)' }),
            animate(`${FADE_ANIMATION_DURATION}ms ease-in-out`, style({ opacity: 1, transform: 'scale(1)' }))
        ];
    }
    fadeOut() {
        return [
            style({ opacity: '*', transform: '*' }),
            animate(`${FADE_ANIMATION_DURATION}ms ease-in-out`, style({ opacity: 0, transform: 'scale(0.8)' }))
        ];
    }
    removeOverlay() {
        this.overlayRef.detach();
        this.overlayRef.dispose();
    }
    destroyAnimation() {
        if (this.fadeInPlayer) {
            this.fadeInPlayer.destroy();
        }
        if (this.fadeOutPlayer) {
            this.fadeOutPlayer.destroy();
        }
    }
    destroyAccessibilityServices() {
        this.tooltipFocusMonitorSubscription.unsubscribe();
        // eslint-disable-next-line @angular-eslint/no-lifecycle-call
        this.tooltipA11yParametersService.ariaDescriber.ngOnDestroy();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipDirective, deps: [{ token: TooltipA11yParametersService }, { token: i2$1.Overlay }, { token: ElementRef }, { token: Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillTooltipDirective, isStandalone: true, selector: "[windmill-tooltip]", inputs: { tooltipText: "tooltipText", tooltipPosition: "tooltipPosition", tooltipShowDelay: "tooltipShowDelay", tooltipHideDelay: "tooltipHideDelay", tooltipClassName: "tooltipClassName", tooltipColor: "tooltipColor", isTooltipDisabled: "isTooltipDisabled" }, host: { listeners: { "mouseenter": "show()", "document:keydown.escape": "handleEscapeKey()", "touchend": "hide()", "mouseleave": "onMouseLeave($event)" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmill-tooltip]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: TooltipA11yParametersService }, { type: i2$1.Overlay }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [Renderer2]
                }] }], propDecorators: { tooltipText: [{
                type: Input
            }], tooltipPosition: [{
                type: Input
            }], tooltipShowDelay: [{
                type: Input
            }], tooltipHideDelay: [{
                type: Input
            }], tooltipClassName: [{
                type: Input
            }], tooltipColor: [{
                type: Input
            }], isTooltipDisabled: [{
                type: Input
            }], show: [{
                type: HostListener,
                args: ['mouseenter']
            }], handleEscapeKey: [{
                type: HostListener,
                args: ['document:keydown.escape']
            }], hide: [{
                type: HostListener,
                args: ['touchend']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave', ['$event']]
            }] } });

const moduleComponents = [WindmillTooltipDirective];
class WindmillTooltipModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipModule, imports: [CommonModule, OverlayModule, WindmillTooltipDirective], exports: [WindmillTooltipDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipModule, imports: [CommonModule, OverlayModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, OverlayModule, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WindmillTooltipDirective, WindmillTooltipModule };
